import qs from "qs";
import { emptyApi } from "./emptyApi";
import {
  NexusTracker,
  NexusTrackerTransactionsResponse,
} from "types/Models/nexusTracker";
import { fileAndFolder } from "types/Models/documents";
import { PersonaAutofill } from "types/Models/personaAutofill";

export type Registration = {
  state_registration_id: string;
  state: string;
  tax_detail_id: string;
  request_date?: string;
  status?: string;
};

export type PhysicalNexusTracker = {
  employees_in_us: string[];
  warehouse_in_us: string[];
  office_in_us: string[];
  office_present: boolean | null;
  employees_present: boolean | null;
  warehouse_present: boolean | null;
};

export type PhysicalNexusTrackerPayload = {
  employees_in_us?: string[];
  warehouse_in_us?: string[];
  office_in_us?: string[];
  office_present: boolean | null;
  employees_present: boolean | null;
  warehouse_present: boolean | null;
};

export type SalesTaxBusinessPayload = {
  owner_id: string;
  persona_details?: PersonaAutofill;
  business_address_id?: string;
  mailing_address_id?: string;
  formation_type_id?: string;
};

export type NexusTrackerCountResponse = {
  MONITORING: number;
  APPROACHING: number;
  EXPOSED: number;
  ALL: number;
};

export type NexusTrackerSummaryResponse = {
  tracked_count: number;
  exempted_count: number;
  exempted_states: string[];
  status_counts: {
    EXPOSED: number;
    APPROACHING: number;
    REGISTERING: number;
    REMITTING: number;
    MONITORING: number;
  };
  registered_count: number;
  registration_status_counts: {
    PENDING_DETAILS: number;
    PROCESSING: number;
  };
  exposed_not_registered_count: number;
};

type SidrDetails = {
  sidr_login_email: string;
  sidr_api_key: string;
};

export type NexusStatusForStateResponse = {
  nexus_status: string;
  nexus_registration_status: string | null;
  nexus_registration_id: string | null;
  nexus_state_id: string;
  nexus_state_name: string;
  nexus_cart_status: string | null;
  nexus_physical_presence_confirmed_absent: boolean | null;
  launched_task_id: string | null;
};

export const salesNexusTracker = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getAllNexusTrackers: build.query<
      NexusTracker,
      {
        pageNum: number;
        limit?: number;
        search?: string | null;
        entityId: string;
        status_filter?: string;
        nexus_type?: string;
        registration_status?: string;
        state_registration_status?: string;
        exclude_exempt?: boolean;
      }
    >({
      query: ({
        entityId,
        pageNum,
        limit,
        search,
        status_filter,
        nexus_type,
        registration_status,
        state_registration_status,
        exclude_exempt,
      }) => {
        let queryUrl = qs.stringify(
          {
            page_num: pageNum,
            limit,
            search,
            status_filter,
            nexus_type,
            registration_status,
            state_registration_status,
            exclude_exempt,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/salestax/entity/${entityId}/nexus_tracker/${queryUrl}`,
        };
      },
      providesTags: (result) => (result ? ["NEXUSTRACKER"] : []),
    }),
    getNexusTrackerTransactions: build.query<
      NexusTrackerTransactionsResponse,
      {
        entityId: string;
        jurisdictionName: string;
        year: number;
        pageNum?: number;
      }
    >({
      query: ({ entityId, jurisdictionName, pageNum = 1, year }) => {
        let queryUrl = qs.stringify(
          {
            jurisdiction_name: jurisdictionName,
            year,
            page_num: pageNum,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/salestax/entity/${entityId}/nexus_tracker/transactions${queryUrl}`,
        };
      },
      providesTags: ["NEXUSTRACKER"],
    }),
    uploadBulkSalesTaxInvoice: build.mutation<
      {
        bulk_file_id: string;
      },
      {
        entityId: string;
        file: File;
      }
    >({
      query: ({ entityId, file }) => {
        const formData = new FormData();
        formData.append("excel", file);

        return {
          url: `/api/inkle/salestax/entity/${entityId}/bulk_invoices/`,
          method: "POST",
          body: formData,
        };
      },
    }),
    populateBulkSalesTaxInvoice: build.mutation<
      fileAndFolder[],
      {
        entityId: string;
        bulkFileId: string;
      }
    >({
      query: ({ entityId, bulkFileId }) => {
        return {
          url: `/api/inkle/salestax/entity/${entityId}/populate_bulk_invoices/`,
          method: "POST",
          body: { bulk_file_id: bulkFileId },
        };
      },
      invalidatesTags: ["NEXUSTRACKER"],
    }),
    createSalesTaxBusiness: build.mutation<
      SalesTaxBusinessPayload,
      { entityId: string; payload: SalesTaxBusinessPayload }
    >({
      query: ({ entityId, payload }) => ({
        url: `/api/inkle/salestax/entity/${entityId}/business/`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["SALES_TAX_BUSINESS" as const],
    }),

    getSalexTaxBusiness: build.query<
      SalesTaxBusinessPayload,
      { entityId: string }
    >({
      query: ({ entityId }) => ({
        url: `/api/inkle/salestax/entity/${entityId}/business/`,
      }),
      providesTags: ["SALES_TAX_BUSINESS" as const],
    }),
    updateSalesTaxBusiness: build.mutation<
      SalesTaxBusinessPayload,
      { entityId: string; payload: SalesTaxBusinessPayload }
    >({
      query: ({ entityId, payload }) => ({
        url: `/api/inkle/salestax/entity/${entityId}/business/`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["SALES_TAX_BUSINESS" as const],
    }),

    updateSalesTaxRegistration: build.mutation<
      SalesTaxBusinessPayload,
      {
        entityId: string;
        registration_id: string;
        payload: { tax_detail_id: string };
      }
    >({
      query: ({ entityId, payload, registration_id }) => ({
        url: `/api/inkle/salestax/entity/${entityId}/registrations/${registration_id}/`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["SALES_TAX_BUSINESS" as const],
    }),
    getPhysicalNexusTracker: build.query<
      PhysicalNexusTracker,
      { entityId: string }
    >({
      query: ({ entityId }) => ({
        url: `/api/inkle/salestax/entity/${entityId}/nexus_tracker/physical_nexus/`,
      }),
      providesTags: ["PHYSICAL_NEXUS_TRACKER"],
    }),
    updatePhysicalNexusTracker: build.mutation<
      PhysicalNexusTrackerPayload,
      {
        entityId: string;
        payload: PhysicalNexusTracker;
      }
    >({
      query: ({ entityId, payload }) => ({
        url: `/api/inkle/salestax/entity/${entityId}/nexus_tracker/physical_nexus/`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["PHYSICAL_NEXUS_TRACKER", "NEXUSTRACKER"],
    }),
    updatePhysicalNexusForState: build.mutation<
      { status: string },
      {
        entityId: string;
        stateId: string;
        payload: {
          office_present: boolean;
          warehouse_present: boolean;
          employees_present: boolean;
        };
      }
    >({
      query: ({ entityId, stateId, payload }) => ({
        url: `/api/inkle/salestax/entity/${entityId}/nexus_tracker/state/${stateId}/physical_nexus/`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["NEXUSTRACKER"],
    }),
    getNexusTrackerCount: build.query<
      NexusTrackerCountResponse,
      { entityId: string }
    >({
      query: ({ entityId }) => ({
        url: `/api/inkle/salestax/entity/${entityId}/nexus_tracker/count/`,
      }),
      providesTags: ["PHYSICAL_NEXUS_TRACKER", "NEXUSTRACKER"],
    }),
    getNexusTrackerSummary: build.query<
      NexusTrackerSummaryResponse,
      { entityId: string }
    >({
      query: ({ entityId }) => ({
        url: `/api/inkle/salestax/entity/${entityId}/nexus_tracker/summary/`,
      }),
      providesTags: ["NEXUSTRACKER"],
    }),
    updateSidrDetails: build.mutation<
      SidrDetails,
      {
        entityId: string;
        payload: SidrDetails;
      }
    >({
      query: ({ entityId, payload }) => ({
        url: `/api/inkle/salestax/entity/${entityId}/api_detail/`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["SIDR_DETAILS"],
    }),
    getSidrDetails: build.query<SidrDetails, { entityId: string }>({
      query: ({ entityId }) => ({
        url: `/api/inkle/salestax/entity/${entityId}/api_detail/`,
      }),
      providesTags: ["SIDR_DETAILS" as const],
    }),
    getNexusStatusForState: build.query<
      NexusStatusForStateResponse,
      { entityId: string; stateName: string }
    >({
      query: ({ entityId, stateName }) => {
        const queryUrl = qs.stringify(
          { state_name: stateName },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/salestax/entity/${entityId}/nexus_tracker/state_status/${queryUrl}`,
        };
      },
      providesTags: ["NEXUSTRACKER"],
    }),
  }),
});

export const {
  useGetAllNexusTrackersQuery,
  useGetNexusTrackerTransactionsQuery,
  useUploadBulkSalesTaxInvoiceMutation,
  usePopulateBulkSalesTaxInvoiceMutation,
  useCreateSalesTaxBusinessMutation,
  useGetSalexTaxBusinessQuery,
  useUpdateSalesTaxBusinessMutation,
  useUpdateSalesTaxRegistrationMutation,
  useGetPhysicalNexusTrackerQuery,
  useUpdatePhysicalNexusTrackerMutation,
  useUpdatePhysicalNexusForStateMutation,
  useGetNexusTrackerCountQuery,
  useGetNexusTrackerSummaryQuery,
  useUpdateSidrDetailsMutation,
  useGetSidrDetailsQuery,
  useGetNexusStatusForStateQuery,
} = salesNexusTracker;
